import { getApiBaseUrl } from '../config/constants';

const API_BASE_URL = getApiBaseUrl();

export interface Meeting {
  meeting_code: string;
  name: string;
  program_id: string;
  kind: string;
  description?: string;
  format?: string;
  location?: string;
  schedule_day?: string;
  schedule_start_time?: string;
  schedule_duration_minutes?: number;
  timezone?: string;
  link_visibility?: string;
  /** Who can join: anyone | meeting_key | approval_required | invite_only. Default (legacy) anyone. */
  join_rule?: string;
  workshop_first_session_date?: string;
  workshop_duration_minutes?: number;
  created_at?: string;
  updated_at?: string;
  /** Short tagline for list; if absent, display format in first column. API may return tag_line (snake_case). */
  tagline?: string;
  tag_line?: string;
  /** open = Open to Visitors, closed = Those Seeking Help */
  access_type?: 'open' | 'closed';
  /** Men | Mixed | Transgender | Women | women_lgbtq */
  gender?: string;
  language?: string;
  /** When format is telemeeting */
  telemeeting_dial_in?: string;
  telemeeting_passcode?: string;
  /** Pass phrase (only returned to meeting admins for Settings) */
  join_key?: string;
}

export interface CreateMeetingParams {
  name: string;
  meeting_code?: string;  // optional; server assigns code (e.g. SAA7)
  program_id?: string;
  kind?: string;
  description?: string;
  format?: string;
  location?: string;
  schedule_day?: string;
  schedule_start_time?: string;
  schedule_duration_minutes?: number;
  schedule_duration_custom?: string;
  timezone?: string;
  link_visibility?: string;
  workshop_first_session_date?: string;
  workshop_duration_minutes?: number;
  workshop_duration_custom?: string;
  tagline?: string;
  access_type?: 'open' | 'closed';
  gender?: string;
  language?: string;
  telemeeting_dial_in?: string;
  telemeeting_passcode?: string;
}

class MeetingService {
  private getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private async makePublicRequest(endpoint: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(err.detail || err.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  private async makeAuthRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    const token = this.getAuthToken();
    if (!token) throw new Error('Please log in.');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(options.headers as Record<string, string>),
      },
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(err.detail || err.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  async getMeetingByCode(code: string): Promise<Meeting> {
    const normalized = code.trim().toUpperCase();
    const { meeting } = await this.makePublicRequest(`/meetings/by-code/${encodeURIComponent(normalized)}`);
    return meeting;
  }

  /** Get meeting by code with auth; returns join_key when caller is meeting admin (for Settings). */
  async getMeetingByCodeForAdmin(code: string): Promise<Meeting> {
    const normalized = code.trim().toUpperCase();
    const { meeting } = await this.makeAuthRequest(`/meetings/by-code/${encodeURIComponent(normalized)}`);
    return meeting;
  }

  /**
   * Resolve join-by-URL: validate meeting code + pass phrase; returns minimal meeting info or throws with friendly message.
   * Public (no auth). Use before showing "Join [name]?" confirm.
   */
  async getMeetingJoinByUrl(meetingCode: string, passPhrase: string): Promise<{ meeting_code: string; name: string }> {
    const code = meetingCode.trim().toUpperCase();
    const res = await this.makePublicRequest(
      `/meetings/join/${encodeURIComponent(code)}/${encodeURIComponent(passPhrase)}`
    );
    return res.meeting;
  }

  async checkMember(code: string): Promise<boolean> {
    const status = await this.getMemberStatus(code);
    return status.is_member;
  }

  async getMemberStatus(code: string): Promise<{ is_member: boolean; is_admin: boolean }> {
    const normalized = code.trim().toUpperCase();
    const res = await this.makeAuthRequest(`/meetings/check-member/${encodeURIComponent(normalized)}`);
    return { is_member: !!res.is_member, is_admin: !!res.is_admin };
  }

  /** Optional preferences when adding yourself to a meeting (join-preferences step). Pass join_key when meeting uses meeting_key join rule. */
  async joinMeeting(
    code: string,
    preferences?: {
      display_preference?: string;
      include_on_printed_phone_list?: boolean;
      email_notifications_enabled?: boolean;
      sponsor_willingness?: string;
      /** Required when meeting join_rule is meeting_key */
      join_key?: string;
    }
  ): Promise<void> {
    const normalized = code.trim().toUpperCase();
    const body: Record<string, unknown> = preferences
      ? {
          display_preference: preferences.display_preference ?? 'name_only',
          include_on_printed_phone_list: preferences.include_on_printed_phone_list ?? false,
          email_notifications_enabled: preferences.email_notifications_enabled ?? true,
          sponsor_willingness: preferences.sponsor_willingness ?? 'no',
        }
      : {};
    if (preferences?.join_key !== undefined && preferences.join_key !== '') {
      body.join_key = preferences.join_key;
    }
    await this.makeAuthRequest(`/meetings/join/${encodeURIComponent(normalized)}`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async listMembers(code: string): Promise<{ user_id: string; display_name: string; is_admin: boolean; joined_at?: string }[]> {
    const normalized = code.trim().toUpperCase();
    const { members } = await this.makeAuthRequest(`/meetings/${encodeURIComponent(normalized)}/members`);
    return members ?? [];
  }

  async removeMember(code: string, userId: string): Promise<void> {
    const normalized = code.trim().toUpperCase();
    await this.makeAuthRequest(
      `/meetings/${encodeURIComponent(normalized)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  }

  async updateMeeting(
    code: string,
    updates: {
      name?: string;
      description?: string;
      join_rule?: string;
      join_key?: string;
      regenerate_pass_phrase?: boolean;
      tagline?: string;
      access_type?: string;
      gender?: string;
      language?: string;
      format?: string;
      location?: string;
      telemeeting_dial_in?: string;
      telemeeting_passcode?: string;
    }
  ): Promise<void> {
    const normalized = code.trim().toUpperCase();
    const body: Record<string, string | boolean> = {};
    if (updates.name !== undefined) body.name = updates.name;
    if (updates.description !== undefined) body.description = updates.description ?? '';
    if (updates.join_rule !== undefined) body.join_rule = updates.join_rule;
    if (updates.join_key !== undefined) body.join_key = updates.join_key;
    if (updates.regenerate_pass_phrase === true) body.regenerate_pass_phrase = true;
    if (updates.tagline !== undefined) body.tagline = updates.tagline ?? '';
    if (updates.access_type !== undefined) body.access_type = updates.access_type;
    if (updates.gender !== undefined) body.gender = updates.gender ?? '';
    if (updates.language !== undefined) body.language = updates.language ?? '';
    if (updates.format !== undefined) body.format = updates.format;
    if (updates.location !== undefined) body.location = updates.location ?? '';
    if (updates.telemeeting_dial_in !== undefined) body.telemeeting_dial_in = updates.telemeeting_dial_in ?? '';
    if (updates.telemeeting_passcode !== undefined) body.telemeeting_passcode = updates.telemeeting_passcode ?? '';
    await this.makeAuthRequest(`/meetings/${encodeURIComponent(normalized)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  async createMeeting(params: CreateMeetingParams): Promise<{ meeting_code: string }> {
    const body: Record<string, unknown> = {
      name: params.name,
      program_id: params.program_id || 'saa',
      kind: params.kind || 'meeting',
      description: params.description || '',
      format: params.format || 'online',
      location: params.location ?? '',
      schedule_day: params.schedule_day || null,
      schedule_start_time: params.schedule_start_time || null,
      schedule_duration_minutes: params.schedule_duration_minutes ?? null,
      schedule_duration_custom: params.schedule_duration_custom || null,
      timezone: params.timezone || null,
      link_visibility: params.link_visibility || 'everyone',
      tagline: params.tagline ?? '',
      access_type: params.access_type ?? 'open',
      gender: params.gender ?? '',
      language: params.language ?? '',
    };
    if (params.kind === 'workshop') {
      body.workshop_first_session_date = params.workshop_first_session_date || null;
      body.workshop_duration_minutes = params.workshop_duration_minutes ?? null;
      body.workshop_duration_custom = params.workshop_duration_custom || null;
    }
    if (params.format === 'telemeeting') {
      body.telemeeting_dial_in = params.telemeeting_dial_in ?? '';
      body.telemeeting_passcode = params.telemeeting_passcode ?? '';
    }
    return this.makeAuthRequest('/meetings', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async listMeetings(programId?: string): Promise<Meeting[]> {
    const qs = programId ? `?program_id=${encodeURIComponent(programId)}` : '';
    const { meetings } = await this.makePublicRequest(`/meetings${qs}`);
    return meetings || [];
  }

  async getHomeMeetings(): Promise<Meeting[]> {
    const { meetings } = await this.makeAuthRequest('/meetings/home');
    return meetings || [];
  }

  async saveToHome(code: string): Promise<void> {
    const normalized = code.trim().toUpperCase();
    await this.makeAuthRequest(`/meetings/home/${encodeURIComponent(normalized)}`, { method: 'POST' });
  }

  async removeFromHome(code: string): Promise<void> {
    const normalized = code.trim().toUpperCase();
    await this.makeAuthRequest(`/meetings/home/${encodeURIComponent(normalized)}`, { method: 'DELETE' });
  }

  async updateHomeOrder(codes: string[]): Promise<void> {
    await this.makeAuthRequest('/meetings/home/order', {
      method: 'PUT',
      body: JSON.stringify({ meeting_codes: codes }),
    });
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('auth_token');
  }
}

export const meetingService = new MeetingService();
